import { Issue } from '../headersegment/interfaces';
import { parseAndReturnDate, formatDateHyphDMY, formatTime, isCritical, formatDateTime, parseAndFormatDateYMD, camelCaseItFilter } from './filters';

interface FormOptions {
  key: string;
  formatter: keyof typeof SearchParameterFormatter;
  objectKey?: string;
}

export const validInt = (val: any): number => {
  const parsedVal = parseInt(val, 10);
  return isNaN(parsedVal) ? 0 : Math.max(0, parsedVal);
};

export const getSearchParams = (routeParams: Record<string, any>): string[] => {
  return Object.entries(routeParams)
    .filter(([key]) => !['weekago', 'yesterdaystartofday', 'tomorrowendofday'].includes(key.toLowerCase()))
    .map(([key, value]) => `${key.toLowerCase()}=${value}`);
};

export const toSearchParams = (form: Record<string, any>): string[] => {
  const options: Record<string, FormOptions> = {
    assignee: { key: 'assignee', formatter: 'objectField', objectKey: 'id' },
    recordlocator: { key: 'rec', formatter: 'none' },
    firstname: { key: 'fname', formatter: 'none' },
    lastname: { key: 'lname', formatter: 'none' },
    airline: { key: 'airline', formatter: 'objectField', objectKey: 'fs' },
    dateoftravel: { key: 'dateoftravel', formatter: 'date' },
    resolvebymin: { key: 'resolvebymin', formatter: 'dateTime' },
    resolvebymax: { key: 'resolvebymax', formatter: 'dateTime' },
    starttime: { key: 'starttime', formatter: 'dateTime' },
    endtime: { key: 'endtime', formatter: 'dateTime' },
    dateoftravelbegin: { key: 'dateoftravelbegin', formatter: 'date' },
    dateoftravelend: { key: 'dateoftravelend', formatter: 'date' },
    depairport: { key: 'depairport', formatter: 'objectField', objectKey: 'fs' },
    arrairport: { key: 'arrairport', formatter: 'objectField', objectKey: 'fs' },
    tripgroup: { key: 'tripgroup', formatter: 'none' },
    waiver: { key: 'waiver', formatter: 'objectField', objectKey: 'id' },
  };

  return Object.entries(form)
    .map(([key, value]) => {
      if (!value) return null;

      const lowerKey = key.toLowerCase();
      const paramOptions = options[lowerKey];
      if (!paramOptions) {
        return { key: lowerKey, value };
      }

      const newValue = SearchParameterFormatter[paramOptions.formatter](value, paramOptions);
      return { key: paramOptions.key, value: newValue };
    })
    .filter((pair) => !!pair?.key && !!pair?.value)
    .map((pair) => `${pair?.key}=${pair?.value}`);
};

export const eachResult = (results: any[], transformers: ((result: any) => void)[]): any[] => {
  if (!Array.isArray(transformers)) {
    throw new Error('transformers not an array of functions!');
  }
  return results.map((result) => {
    result.selected = result.selected ?? false;
    transformers.forEach((transformer) => transformer(result));
    return result;
  });
};

export const loadTripsForm = (routeParams: Record<string, any>): Record<string, any> => {
  return {
    airline: routeParams?.airline?.toUpperCase() ?? null,
    arrAirport: routeParams?.arrairport?.toUpperCase() ?? null,
    dateOfTravelBegin: routeParams?.dateoftravelbegin
      ? parseAndReturnDate(routeParams.dateoftravelbegin)
      : routeParams.weekAgo
      ? parseAndReturnDate(routeParams.weekAgo)
      : null,
    dateOfTravelEnd: routeParams?.dateoftravelend
      ? parseAndReturnDate(routeParams.dateoftravelend)
      : null,
    depAirport: routeParams?.depairport?.toUpperCase() ?? null,
    email: routeParams?.email ?? null,
    firstName: routeParams?.fname ?? null,
    flightNumber: routeParams?.flightnumber ? Number(routeParams.flightnumber) : null,
    lastName: routeParams?.lname ?? null,
    limit: routeParams?.limit ?? '',
    recordLocator: routeParams?.rec ?? null,
    tripGroup: routeParams?.tripgroup ?? null,
    waiver: routeParams?.waiver ? { id: routeParams.waiver } : null,
  };
};

export const loadIssuesForm = (
  routeParams: Record<string, any>,
  assignees: { id: number }[] = [],
): { advForm: Record<string, any>; resolveByMaxSetDefaultTime: boolean } => {
  const advForm = {
    airline: routeParams?.airline?.toUpperCase() ?? null,
    assignee: routeParams?.assignee || '',
    dateOfTravel: routeParams?.dateoftravel ? parseAndReturnDate(routeParams.dateoftravel) : null,
    depAirport: routeParams?.depairport?.toUpperCase() ?? null,
    flightNumber: routeParams?.flightnumber ? Number(routeParams.flightnumber) : null,
    groupName: routeParams?.groupname ?? null,
    limit: routeParams?.limit ?? '',
    type: routeParams?.type?.toLowerCase() ?? '',
    recordLocator: routeParams?.rec ?? null,
    resolveByMin: routeParams?.resolvebymin ? parseAndReturnDate(routeParams.resolvebymin, 'YYYY-MM-DDTHH:mm:ssZZ') : null,
    resolveByMax: routeParams?.resolvebymax ? parseAndReturnDate(routeParams.resolvebymax, 'YYYY-MM-DDTHH:mm:ssZZ') : null,
    state: routeParams?.state?.toLowerCase() ?? '',
  };

  const resolveByMaxSetDefaultTime = !routeParams?.resolvebymax;

  if (routeParams.assignee && assignees.length) {
    const match = assignees.find((assignee) => assignee.id === parseInt(routeParams.assignee, 10));
    if (match) advForm.assignee = match;
  }

  return { advForm, resolveByMaxSetDefaultTime };
};

export const isSort = (sortState: { sort: string; sortAsc: boolean }, criteria: string, order?: string): boolean => {
  const { sort, sortAsc } = sortState;

  if (criteria === sort) {
    if (order) {
      if (order === 'asc') {
        return sortAsc;
      }
      if (order === 'desc') {
        return !sortAsc;
      }
    } else {
      return true;
    }
  }
  return false;
};

export const formatResolveByTimes = (issue: Issue): void => {
  issue.resolveByTimeIsCritical = isCritical(issue.resolveByTime);
  issue.resolveByTimeFormatted = issue.resolveByTime ? formatDateTime(issue.resolveByTime) : 'N/A';
};

export const addTotalIssuesFieldToEachTrip = (trip: any): void => {
  trip.totalIssues = 0;
  if (Array.isArray(trip.issues)) {
    trip.totalIssues += trip.issues.length;
  }
  if (Array.isArray(trip.legs)) {
    trip.legs.forEach((leg: any) => {
      if (Array.isArray(leg.flights)) {
        leg.flights.forEach((flight: any) => {
          if (Array.isArray(flight.issues)) {
            trip.totalIssues += flight.issues.length;
          }
        });
      }
    });
  }
};

export const formatFlightTimes = (trip: any): void => {
  if (Array.isArray(trip.legs)) {
    trip.legs.forEach((leg: any) => {
      if (Array.isArray(leg.flights)) {
        leg.flights.forEach((flight: any) => {
          flight.departureTimeDMY = formatDateHyphDMY(flight.departureTime);
          flight.departureTime = formatTime(flight.departureTime);
          flight.arrivalTime = formatTime(flight.arrivalTime);
        });
      }
    });
  }
};

export const SearchParameterFormatter = {
  none: (value: any): any => value,

  date: (value: any): string => parseAndFormatDateYMD(value),

  dateTime: (value: any): string => parseAndFormatDateYMD(value, true),

  camelCase: (value: any): string => camelCaseItFilter(value),

  objectField: (value: any, options: { objectKey?: string }): any => {
    if (!(value instanceof Object)) {
      return value;
    }
    return options.objectKey && value?.[options.objectKey] != null ? value[options.objectKey] : null;
  },
};
